/**
 * The requested translation language, as found in the URL.
 *
 * Four states rather than `string | null`, because a missing parameter and an
 * explicit `lang=en` have to behave differently: a missing one falls back to the
 * language resolved earlier in the session, while `lang=en` deliberately clears
 * it. Collapsing them would leave no way back to the untranslated original.
 *
 * `english` means the original, not the English language: a regional English is
 * an ordinary translation target and resolves to `language`.
 */
export type LangParam =
    | { kind: 'language', tag: string }
    | { kind: 'english' }
    | { kind: 'absent' }
    | { kind: 'invalid' };

// Two letters, optionally a region subtag: es, pt-br, zh-hans.
const LANG_TAG_PATTERN = /^[a-z]{2}(-[a-z]{2,4})?$/;

/**
 * Classify a raw language tag, whatever its source.
 *
 * Shared between the URL and session storage so a stale or tampered stored value
 * is held to the same rules as one arriving in a link.
 */
export function classifyLangTag(rawTag: string | null): LangParam {
    if (rawTag === null) {
        return { kind: 'absent' };
    }
    const tag = rawTag.trim().toLowerCase();
    if (!LANG_TAG_PATTERN.test(tag)) {
        return { kind: 'invalid' };
    }
    // Bare "en" only. Regional Englishes are Weglot targets like any other language; it is
    // the unqualified tag that means the untranslated original, and so keeps an English
    // session from fetching Weglot at all.
    if (tag === 'en') {
        return { kind: 'english' };
    }
    return { kind: 'language', tag };
}

function readLangParam(query: string): string | null {
    return new URLSearchParams(query).get('lang');
}

/**
 * Read the requested translation language from the URL.
 *
 * The app routes on the hash, so its query sits inside the fragment
 * ("#/overview?lang=es") and `location.search` is normally empty. Taking
 * everything after the first "?" in the hash also covers the legacy
 * "#!/overview" form, because the "!" falls before the query either way.
 *
 * `location.search` is read as a fallback for a link written the other way round
 * ("?lang=es#/overview"). `handleLegacyMithrilLocation` folds a pre-fragment
 * query into the hash before the app starts, so the fallback only matters when
 * this runs earlier than that.
 */
export default function getWeglotLang(location: { search: string, hash: string }): LangParam {
    const hashQueryIndex = location.hash.indexOf('?');
    const hashQuery = hashQueryIndex === -1 ? '' : location.hash.slice(hashQueryIndex + 1);
    return classifyLangTag(readLangParam(hashQuery) ?? readLangParam(location.search));
}
